import type { ClassId, ItemInstance, ItemTemplate } from '@shefira/shared';

import { type CssAtlas, loadSpriteAtlas } from '../assets/dom-atlas.js';
import { ItemTooltip, type TooltipDetails } from './item-tooltip.js';
import { isTouch } from './touch.js';

/**
 * The craftsmen's windows (smith, alchemist, smelter) show what they ask for
 * as icons: hovering one, or tapping it on a phone, opens its card with where
 * to come by it. The merchant's shows what he sells, set against what the
 * player wears. One card for all of them, made on first use.
 */

let shared: ItemTooltip | undefined;

function tooltip(): ItemTooltip {
  shared ??= new ItemTooltip();
  return shared;
}

/**
 * A panel does not wear anything: the card judges no level nor class, it
 * only describes. The level requirement shows, never in red.
 */
const ANYONE = { level: Number.MAX_SAFE_INTEGER, classId: 'warrior' } as const;

/** Who reads the card and what it adds: asked on every hover, so it follows the bag. */
export interface HoverContext {
  wearer: { level: number; classId: ClassId };
  details: TooltipDetails;
  /** The very item, when the row stands for one of the bag (its +N, its affixes). */
  instance?: ItemInstance;
}

const CRAFTSMAN: HoverContext = { wearer: ANYONE, details: { sources: true } };

export function describeOnHover(
  element: HTMLElement,
  template: ItemTemplate | undefined,
  context: () => HoverContext = () => CRAFTSMAN,
): void {
  if (template === undefined) return;
  element.classList.add('item--describable');
  element.addEventListener('click', () => {
    if (!isTouch()) return;
    const { wearer, details, instance } = context();
    tooltip().pin(template, instance, wearer, element, [], undefined, details);
  });
  // A tap also fires emulated mouse events: they would open the cursor's
  // card, then close the pinned one.
  element.addEventListener('mouseenter', (event) => {
    if (isTouch()) return;
    const { wearer, details, instance } = context();
    tooltip().show(template, instance, wearer, { x: event.clientX, y: event.clientY }, details);
  });
  element.addEventListener('mousemove', (event) => {
    if (isTouch()) return;
    tooltip().moveTo({ x: event.clientX, y: event.clientY });
  });
  element.addEventListener('mouseleave', () => {
    if (isTouch()) return;
    tooltip().hide();
  });
}

/**
 * A panel that redraws or closes takes the hovered icon away without a
 * `mouseleave`: it calls this so the card goes with it.
 */
export function hideItemHover(): void {
  shared?.hide();
}

/** The icons atlas for the chat's cards, asked once; a failure is asked again next click. */
let linkIcons: Promise<CssAtlas | null> | undefined;

function iconsForLinks(): Promise<CssAtlas | null> {
  linkIcons ??= loadSpriteAtlas('icon.potion').then((icons) => {
    if (icons === null) linkIcons = undefined;
    return icons;
  });
  return linkIcons;
}

/**
 * An item linked in the chat (ADR 0085): a click pins its card beside the
 * link, on any screen, until a click elsewhere or Escape. A click on the link
 * whose card is open closes it.
 */
export function pinItemCard(
  template: ItemTemplate,
  instance: ItemInstance,
  anchor: HTMLElement,
): void {
  const card = tooltip();
  if (card.pinnedTo(anchor)) {
    card.hide();
    return;
  }
  void iconsForLinks().then((icon) => {
    card.pin(template, instance, ANYONE, anchor, [], undefined, { icon });
  });
}
